import { ApiDisabilityProof } from '../api-types/disability-proof.api';
import { ApiNeighborContext } from '../api-types/neighbor.api';
import { DisabilityProof } from '../app-types/disability-proof.app';
import { NeighborContext } from '../app-types/neighbors.app';
import { transformApiDocumentFilesToAppDocumentFiles } from './document.transform';
import { transformApiUserToAppUser } from './user.transform';

/**
 * Converts an API disability proof object to an application disability proof object.
 * @param apiProof The API disability proof object to transform.
 * @returns The transformed application disability proof object.
 */
export function transformApiDisabilityProofToAppDisabilityProof(apiProof: ApiDisabilityProof): DisabilityProof {
	return {
		id: apiProof.id,
		remark: apiProof.remark,
		disabilityType: apiProof.categoryDisabilitySeverity.remark,
		since: new Date(apiProof.statusStart),
		status: {
			limitedUntil: apiProof.statusEnd ? new Date(apiProof.statusEnd) : undefined,
			content: apiProof.categoryDisabilityStatus.remark,
			initial: apiProof.categoryDisabilityStatus.initial,
		},
		documents: transformApiDocumentFilesToAppDocumentFiles(apiProof.documents),
		advisor: transformApiUserToAppUser(apiProof.advisor),
		categoryDisabilitySeverity: {
			id: apiProof.categoryDisabilitySeverity?.id,
			remark: apiProof.categoryDisabilitySeverity?.remark,
			relatedDisabilityStatuses: apiProof.categoryDisabilitySeverity?.relatedDisabilityStatuses?.map((status) => ({
				id: status.id,
				remark: status.remark,
				initial: status.initial,
			})),
		},
		categoryDisabilityStatus: {
			id: apiProof.categoryDisabilityStatus?.id,
			remark: apiProof.categoryDisabilityStatus?.remark,
			relatedDisabilitySeverities: apiProof.categoryDisabilityStatus?.relatedDisabilitySeverities?.map((severity) => ({
				id: severity.id,
				remark: severity.remark,
			})),
			initial: apiProof.categoryDisabilityStatus.initial,
		},
		otherText: apiProof.otherText,
	};
}

/**
 * Converts an array of API disability proofs to application disability proofs.
 * @param apiProofs The array of API disability proofs to transform.
 * @returns A new array of transformed application disability proofs.
 */
export function transformApiDisabilityProofsToAppDisabilityProofs(apiProofs: ApiDisabilityProof[]): DisabilityProof[] {
	return apiProofs.map(transformApiDisabilityProofToAppDisabilityProof);
}

export function transformApiDisabilityProofNeighborsToAppDisabilityProofNeighbors(
	input: ApiNeighborContext<ApiDisabilityProof>,
): NeighborContext<DisabilityProof> {
	return {
		current: transformApiDisabilityProofToAppDisabilityProof(input.current),
		previous: input.previous ? transformApiDisabilityProofToAppDisabilityProof(input.previous) : null,
		next: input.next ? transformApiDisabilityProofToAppDisabilityProof(input.next) : null,
	};
}
